import { addBusinessDaysToPlainDate, type PlainDate } from "../../utils/plain-date.ts";
import { type TypeId, typeid } from "../../utils/typeid.ts";
import type { Company } from "../company/company.ts";
import { buildOrganisationFilePath, type GrafitFile } from "../file/file.ts";
import type { Frozen, NonFrozen } from "../freeze/freezable.ts";
import type { Organisation } from "../organisation/organisation.ts";
import type { Project } from "../project/project.ts";
import type { SupplierDeliverableType } from "../supplier/supplier-deliverable-type.ts";
import type { GrafitUserId } from "../user/user.ts";

export const SUPPLIER_QUOTE_REQUEST_CONTENT_TYPE_ID = "sqr_content" as const;

export type SupplierQuoteRequestContentId = TypeId<typeof SUPPLIER_QUOTE_REQUEST_CONTENT_TYPE_ID>;

export const WANTED_RESPONSE_DATE_DEFAULT_OFFSET_BUSINESS_DAYS = 10;

export function computeDefaultWantedResponseDate(now: PlainDate): PlainDate {
  return addBusinessDaysToPlainDate(now, WANTED_RESPONSE_DATE_DEFAULT_OFFSET_BUSINESS_DAYS);
}

interface SupplierQuoteRequestContentBaseFields {
  id: SupplierQuoteRequestContentId;
  projectId: Project["id"];
  name: string;
  description: string | null;
  wantedResponseDate: PlainDate;
  contactUserId: GrafitUserId;
  emittingCompanyId: Company["id"];
  deliverableType: SupplierDeliverableType;
  createdBy: GrafitUserId;
}

export type SupplierQuoteRequestContent =
  | (SupplierQuoteRequestContentBaseFields & Frozen & { fileId: GrafitFile["id"] })
  | (SupplierQuoteRequestContentBaseFields & NonFrozen & { fileId: null });

export const buildSqrXlsxFilePath = ({ contentId }: { contentId: SupplierQuoteRequestContentId }): string =>
  `sqr-xlsx/${contentId}`;

export const buildSqrXlsxOrganisationFilePath = ({
  organisationId,
  contentId,
}: {
  organisationId: Organisation["id"];
  contentId: SupplierQuoteRequestContentId;
}): string => buildOrganisationFilePath({ organisationId, filePath: buildSqrXlsxFilePath({ contentId }) });

export const SUPPLIER_QUOTE_REQUEST_CONTENT_PATCHABLE_FIELDS = [
  "contactUserId",
  "emittingCompanyId",
  "wantedResponseDate",
  "name",
  "description",
  "deliverableType",
] as const;

export type SupplierQuoteRequestContentPatch = Partial<
  Pick<SupplierQuoteRequestContent, (typeof SUPPLIER_QUOTE_REQUEST_CONTENT_PATCHABLE_FIELDS)[number]>
>;

export interface CreateSupplierQuoteRequestContent {
  projectId: SupplierQuoteRequestContent["projectId"];
  name: SupplierQuoteRequestContent["name"];
  description: SupplierQuoteRequestContent["description"];
  deliverableType?: SupplierQuoteRequestContent["deliverableType"];
}

export const generateSupplierQuoteRequestContentId = (): SupplierQuoteRequestContentId =>
  typeid(SUPPLIER_QUOTE_REQUEST_CONTENT_TYPE_ID);
